/**
 * Pure helpers for attaching images to products, used by the import's
 * "Add images" step and the standalone Add images dialog on Manage Products.
 */

/** Must match the API: POST /products/:id/image rejects anything larger. */
export const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const ACCEPT_ATTRIBUTE = ACCEPTED_IMAGE_TYPES.join(",");

/** "C:\pics\0123 4567.JPG" -> "0123 4567". Only the last extension is removed. */
export function barcodeFromFilename(filename) {
  const base = String(filename || "")
    .split(/[\\/]/)
    .pop();
  const dot = base.lastIndexOf(".");
  return (dot > 0 ? base.slice(0, dot) : base).trim();
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

/** Returns an error message, or null when the file can be uploaded. */
export function validateImageFile(file) {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return "Not a JPEG, PNG or WebP image";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "Larger than the 4 MB limit";
  }
  return null;
}

/**
 * Match dropped files to products by the barcode in the filename.
 *
 * A file matches a product when its name (without extension) equals the
 * barcode, ignoring case, or, failing that, when both reduce to the same
 * run of at least 6 digits (so "0 12345 67890 5.jpg" finds "012345678905").
 * Each product takes at most one file: the first wins and later ones are
 * returned as unmatched, as are invalid files and files matching no product.
 *
 * A file that matches a product which already has an image (`product_image`
 * set) is not uploaded straight away but returned in `conflicts`, so the
 * caller can let the user decide whether to replace it.
 *
 * @param {File[]} files
 * @param {{product_id: number, barcode: string, product_image?: string|null}[]} products
 * @returns {{
 *   matched: {file: File, product: object}[],
 *   conflicts: {file: File, product: object}[],
 *   unmatched: {file: File, reason: string}[],
 * }}
 */
export function matchFilesToProducts(files, products) {
  const byExact = new Map();
  const byDigits = new Map();
  for (const product of products) {
    const barcode = String(product.barcode || "").trim();
    if (!barcode) continue;
    byExact.set(barcode.toLowerCase(), product);
    const digits = digitsOnly(barcode);
    if (digits.length >= 6 && !byDigits.has(digits)) {
      byDigits.set(digits, product);
    }
  }

  const matched = [];
  const conflicts = [];
  const unmatched = [];
  const taken = new Set();

  for (const file of files) {
    const invalid = validateImageFile(file);
    if (invalid) {
      unmatched.push({ file, reason: invalid });
      continue;
    }

    const name = barcodeFromFilename(file.name);
    const digits = digitsOnly(name);
    const product =
      byExact.get(name.toLowerCase()) ??
      (digits.length >= 6 ? byDigits.get(digits) : undefined);

    if (!product) {
      unmatched.push({ file, reason: "No product with this barcode" });
    } else if (taken.has(product.product_id)) {
      unmatched.push({
        file,
        reason: "Another file already matched this product",
      });
    } else {
      taken.add(product.product_id);
      (product.product_image ? conflicts : matched).push({ file, product });
    }
  }

  return { matched, conflicts, unmatched };
}
